// src/scenes/Dashboard/index.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,  Card, CardContent, Typography, Avatar, Chip,
  CircularProgress, Alert, IconButton, Tooltip, Select,
  MenuItem, FormControl, LinearProgress, Divider, Paper,
  useTheme, useMediaQuery
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import {
  TrendingUp, ShoppingCart, People, Inventory,
  PointOfSale, Warning, Refresh, Group,
  LocalShipping, FiberManualRecord, AttachMoney,
  CreditCard, BarChart
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import api from '../../services/auth';

// ─── Utilitaires ─────────────────────────────────────────────────────────────

const fmt = (n) =>
  n == null ? '—' :
  new Intl.NumberFormat('fr-HT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n) + ' HTG';

const fmtNum = (n) =>
  n == null ? '—' : new Intl.NumberFormat('fr-HT').format(n);

const fmtDate = (d) =>
  new Intl.DateTimeFormat('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' }).format(new Date(d));

const fmtDateShort = (d) =>
  new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' }).format(new Date(d));

const COLORS_PIE = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

// ─── Composants de base ───────────────────────────────────────────────────────

function SectionTitle({ icon, title, badge, badgeColor = 'primary' }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
      <Avatar sx={{ bgcolor: 'action.hover', width: 30, height: 30 }}>
        {React.cloneElement(icon, { sx: { fontSize: 16, color: 'text.secondary' } })}
      </Avatar>
      <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: 13, sm: 15 } }}>
        {title}
      </Typography>
      {badge != null && (
        <Chip label={badge} size="small" color={badgeColor} sx={{ height: 18, fontSize: 10 }} />
      )}
    </Box>
  );
}

// ─── KPI Cards ────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color = '#3b82f6', alert = false }) {
  return (
    <Card sx={{
      height: '100%',
      border: alert ? '1px solid #ef4444' : '1px solid rgba(128,128,128,0.15)',
      bgcolor: alert ? 'rgba(239,68,68,0.06)' : 'background.paper',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 }
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box minWidth={0}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: { xs: 9, sm: 10 }, display: 'block' }}
            >
              {label}
            </Typography>
            <Typography
              fontWeight={700}
              sx={{
                mt: 0.5,
                color: alert ? 'error.main' : 'text.primary',
                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                lineHeight: 1.2,
                wordBreak: 'break-word'
              }}
            >
              {value}
            </Typography>
            {sub && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block', fontSize: { xs: 9, sm: 10 } }}>
                {sub}
              </Typography>
            )}
          </Box>
          <Avatar sx={{ bgcolor: `${color}1a`, width: { xs: 34, sm: 40 }, height: { xs: 34, sm: 40 }, flexShrink: 0 }}>
            {React.cloneElement(icon, { sx: { color, fontSize: { xs: 18, sm: 20 } } })}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Section Tendances — pleine largeur ───────────────────────────────────────

function SectionTendances({ data }) {
  const isMobile = useMediaQuery('(max-width:600px)');

  if (!data?.ca_par_jour) return null;

  const today = new Date();
  const days = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dateStr = d.toISOString().split('T')[0];
    const found = data.ca_par_jour.find(x => x.date === dateStr);
    days.push({
      date: dateStr,
      total: found ? parseFloat(found.total) : 0,
      nombre_ventes: found?.nombre_ventes || 0
    });
  }

  const maxCA = Math.max(...days.map(d => d.total), 1);
  const totalSemaine = days.reduce((s, d) => s + d.total, 0);
  const totalVentes = days.reduce((s, d) => s + d.nombre_ventes, 0);
  const moyenneJour = totalSemaine / 7;

  return (
    <Card>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Header avec stats résumées */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
          <SectionTitle icon={<BarChart />} title="Tendances — 7 derniers jours" />
          <Box display="flex" gap={{ xs: 2, sm: 4 }} flexWrap="wrap">
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.8 }}>
                CA semaine
              </Typography>
              <Typography variant="h6" fontWeight={800} color="primary.main" sx={{ lineHeight: 1.2 }}>
                {fmt(totalSemaine)}
              </Typography>
            </Box>
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.8 }}>
                Moy. / jour
              </Typography>
              <Typography variant="h6" fontWeight={800} color="success.main" sx={{ lineHeight: 1.2 }}>
                {fmt(moyenneJour)}
              </Typography>
            </Box>
            <Box textAlign={{ xs: 'left', sm: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: 0.8 }}>
                Ventes totales
              </Typography>
              <Typography variant="h6" fontWeight={800} sx={{ lineHeight: 1.2 }}>
                {fmtNum(totalVentes)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Graphique principal */}
        <Box sx={{ height: { xs: 200, sm: 260, md: 300 }, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={days}
              margin={{ top: 5, right: isMobile ? 5 : 20, left: isMobile ? -20 : 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(128,128,128,0.1)" />
              <XAxis
                dataKey="date"
                tickFormatter={isMobile ? fmtDateShort : fmtDate}
                tick={{ fontSize: isMobile ? 9 : 11 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                yAxisId="left"
                tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                tick={{ fontSize: isMobile ? 9 : 11 }}
                tickLine={false}
                axisLine={false}
                width={isMobile ? 35 : 50}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: isMobile ? 9 : 11 }}
                tickLine={false}
                axisLine={false}
                width={isMobile ? 25 : 35}
              />
              <RechartsTooltip
                formatter={(v, name) => [name === 'CA' ? fmt(v) : fmtNum(v), name]}
                labelFormatter={(d) => fmtDate(d)}
                contentStyle={{
                  borderRadius: 8,
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  fontSize: 12
                }}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="total"
                name="CA"
                stroke="#3b82f6"
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#3b82f6', strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="nombre_ventes"
                name="Ventes"
                stroke="#10b981"
                strokeWidth={2.5}
                dot={{ r: 4, fill: '#10b981', strokeWidth: 0 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Barres journalières mini */}
        <Box display="flex" gap={{ xs: 0.5, sm: 1 }} mt={2} alignItems="flex-end" height={40}>
          {days.map((d, i) => {
            const pct = maxCA > 0 ? (d.total / maxCA) * 100 : 0;
            const isToday = i === 6;
            return (
              <Tooltip key={i} title={`${fmtDateShort(d.date)} — ${fmt(d.total)}`} arrow>
                <Box
                  flex={1}
                  sx={{
                    height: `${Math.max(pct, 4)}%`,
                    bgcolor: isToday ? '#3b82f6' : 'rgba(59,130,246,0.25)',
                    borderRadius: '3px 3px 0 0',
                    cursor: 'default',
                    transition: 'background 0.2s',
                    '&:hover': { bgcolor: isToday ? '#2563eb' : 'rgba(59,130,246,0.45)' },
                    minHeight: 4,
                  }}
                />
              </Tooltip>
            );
          })}
        </Box>
        <Box display="flex" gap={{ xs: 0.5, sm: 1 }} mt={0.5}>
          {days.map((d, i) => (
            <Box key={i} flex={1} textAlign="center">
              <Typography variant="caption" sx={{ fontSize: { xs: 8, sm: 10 }, color: i === 6 ? 'primary.main' : 'text.secondary', fontWeight: i === 6 ? 700 : 400 }}>
                {new Intl.DateTimeFormat('fr-FR', { weekday: 'narrow' }).format(new Date(d.date))}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Section Ventes ───────────────────────────────────────────────────────────

function SectionVentes({ data }) {
  if (!data) return null;
  const pieData = (data.par_methode_paiement || []).map(m => ({
    name: m.methode_paiement,
    value: parseFloat(m.total)
  }));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <SectionTitle
          icon={<ShoppingCart />}
          title="Ventes du jour"
          badge={data.nombre_ventes_annulees > 0 ? `${data.nombre_ventes_annulees} annulées` : null}
          badgeColor="error"
        />
        <Grid container spacing={1.5} mb={2}>
          {[
            { label: 'CA', value: fmt(data.chiffre_affaires), color: 'success.main' },
            { label: 'Ventes', value: fmtNum(data.nombre_ventes), color: 'text.primary' },
            { label: 'Panier moy.', value: fmt(data.panier_moyen), color: 'text.primary' },
            { label: 'Crédits', value: fmt(data.total_credit), color: 'warning.main' },
          ].map(({ label, value, color }) => (
            <Grid item xs={6} key={label}>
              <Box textAlign="center" p={1} sx={{ bgcolor: 'action.hover', borderRadius: 2 }}>
                <Typography fontWeight={800} color={color} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  {value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 11 } }}>
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {pieData.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
              Méthodes de paiement
            </Typography>
            <Box sx={{ height: { xs: 160, sm: 190 }, mt: 1 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={3} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={COLORS_PIE[i % COLORS_PIE.length]} />)}
                  </Pie>
                  <RechartsTooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  <Legend iconSize={8} formatter={(v) => <span style={{ fontSize: 10, textTransform: 'capitalize' }}>{v}</span>} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Stock ────────────────────────────────────────────────────────────

function SectionStock({ data }) {
  if (!data) return null;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <SectionTitle
          icon={<Inventory />}
          title="État du stock"
          badge={data.produits_en_rupture?.count > 0 ? `${data.produits_en_rupture.count} rupture` : null}
          badgeColor="error"
        />
        <Grid container spacing={1} mb={2}>
          {[
            { label: 'Actifs', value: fmtNum(data.total_produits_actifs), bg: 'action.hover', color: 'text.primary' },
            { label: 'Rupture', value: data.produits_en_rupture?.count || 0, bg: 'rgba(239,68,68,0.08)', color: 'error.main' },
            { label: 'Stock faible', value: data.produits_stock_faible?.count || 0, bg: 'rgba(245,158,11,0.08)', color: 'warning.main' },
            { label: 'Achats', value: fmt(data.valeur_stock_total), bg: 'rgba(16,185,129,0.08)', color: 'success.main' },
            { label: 'Ventes', value: fmt(data.valeur_stock_vente), bg: 'rgba(16,185,129,0.08)', color: 'success.main' },
            { label: 'Benefices', value: fmt(data.benefice_potentiel), bg: 'rgba(16,185,129,0.08)', color: 'success.main' },
          ].map(({ label, value, bg, color }) => (
            <Grid item xs={6} key={label}>
              <Box p={1.25} sx={{ bgcolor: bg, borderRadius: 2, textAlign: 'center' }}>
                <Typography fontWeight={700} color={color} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                  {value}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 10 } }}>
                  {label}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {data.produits_en_rupture?.liste?.length > 0 && (
          <Box mb={1.5}>
            <Typography variant="caption" color="error" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
              Ruptures
            </Typography>
            {data.produits_en_rupture.liste.slice(0, 3).map((p, i) => (
              <Box key={i} display="flex" justifyContent="space-between" alignItems="center" py={0.5}
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontSize: { xs: 11, sm: 13 } }} noWrap>{p.produit__nom}</Typography>
                <Chip label={p.succursale__nom} size="small" color="error" variant="outlined" sx={{ height: 16, fontSize: 9, ml: 1, flexShrink: 0 }} />
              </Box>
            ))}
          </Box>
        )}

        {data.produits_stock_faible?.liste?.length > 0 && (
          <Box>
            <Typography variant="caption" color="warning.main" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
              Stock faible
            </Typography>
            {data.produits_stock_faible.liste.slice(0, 3).map((p, i) => (
              <Box key={i} py={0.5} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" sx={{ fontSize: { xs: 11, sm: 13 } }} noWrap flex={1}>{p.produit__nom}</Typography>
                  <Typography variant="caption" color="warning.main" sx={{ flexShrink: 0, ml: 1 }}>
                    {p.stock_actuel}/{p.stock_minimum}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (parseFloat(p.stock_actuel) / parseFloat(p.stock_minimum)) * 100)}
                  color="warning" sx={{ height: 3, borderRadius: 2, mt: 0.5 }}
                />
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Clients ──────────────────────────────────────────────────────────

function SectionClients({ data }) {
  if (!data) return null;
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <SectionTitle icon={<People />} title="Clients & Crédits" />
        <Grid container spacing={1} mb={2}>
          <Grid item xs={6}>
            <Box p={1.25} sx={{ bgcolor: 'action.hover', borderRadius: 2, textAlign: 'center' }}>
              <Typography fontWeight={700} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>{fmtNum(data.total_clients)}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 10 } }}>
                {data.nouveaux_aujourd_hui > 0 ? `+${data.nouveaux_aujourd_hui} aujourd'hui` : 'Clients'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box p={1.25} sx={{ bgcolor: 'rgba(239,68,68,0.08)', borderRadius: 2, textAlign: 'center' }}>
              <Typography fontWeight={700} color="error.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                {fmt(data.total_credit_en_cours)}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 10 } }}>
                {data.nombre_clients_avec_credit} clients
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {data.top_debiteurs?.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
              Top débiteurs
            </Typography>
            {data.top_debiteurs.map((c, i) => (
              <Box key={i} py={0.75} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={600} noWrap flex={1} sx={{ fontSize: { xs: 11, sm: 13 } }}>
                    {c.prenom} {c.nom}
                  </Typography>
                  <Typography variant="body2" color="error.main" fontWeight={700} sx={{ flexShrink: 0, fontSize: { xs: 11, sm: 13 } }}>
                    {fmt(c.solde_credit)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (c.solde_credit / c.limite_credit) * 100)}
                  color="error" sx={{ height: 3, borderRadius: 2, mt: 0.5 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 9 }}>
                  Limite: {fmt(c.limite_credit)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Caisses ──────────────────────────────────────────────────────────

function SectionSessions({ data }) {
  if (!data) return null;
  const ouvertes = data.sessions_ouvertes;
  const ecart = data.ecart_total_aujourd_hui;

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Avatar sx={{ bgcolor: 'action.hover', width: 30, height: 30 }}>
            <PointOfSale sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Avatar>
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: { xs: 13, sm: 15 } }}>Caisses</Typography>
          {ouvertes?.count > 0 && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <FiberManualRecord sx={{
                fontSize: 9, color: 'success.main',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }
              }} />
              <Chip label={`${ouvertes.count} ouvertes`} size="small" color="success" sx={{ height: 18, fontSize: 10 }} />
            </Box>
          )}
        </Box>

        {ouvertes?.liste?.map((s, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1.25, mb: 1, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
              <Box minWidth={0}>
                <Typography variant="body2" fontWeight={700} noWrap sx={{ fontSize: { xs: 11, sm: 13 } }}>
                  {s.caissier_nom}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: { xs: 9, sm: 11 } }}>
                  {s.succursale_nom} · {s.duree}
                </Typography>
              </Box>
              <Box textAlign="right" flexShrink={0}>
                <Typography variant="body2" fontWeight={700} color="success.main" sx={{ fontSize: { xs: 11, sm: 13 } }}>
                  {fmt(s.chiffre_affaires)}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 11 } }}>
                  {s.nombre_ventes} ventes
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}

        {ouvertes?.count === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2} sx={{ fontSize: { xs: 11, sm: 13 } }}>
            Aucune caisse ouverte
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />
        <Box display="flex" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 11 } }}>
            Sessions fermées aujourd'hui
          </Typography>
          <Typography variant="caption" fontWeight={700} sx={{ fontSize: { xs: 9, sm: 11 } }}>
            {data.sessions_fermees_aujourd_hui}
          </Typography>
        </Box>
        {ecart !== 0 && ecart != null && (
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 11 } }}>Écart total</Typography>
            <Typography variant="caption" fontWeight={700} color={ecart < 0 ? 'error.main' : 'success.main'} sx={{ fontSize: { xs: 9, sm: 11 } }}>
              {ecart > 0 ? '+' : ''}{fmt(ecart)}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Équipe ───────────────────────────────────────────────────────────

function SectionEquipe({ data }) {
  if (!data) return null;
  const medals = ['🥇', '🥈', '🥉'];
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <SectionTitle icon={<Group />} title="Équipe"
          badge={`${data.actifs_aujourd_hui} actifs`} badgeColor="info" />
        <Box display="flex" flexWrap="wrap" gap={0.75} mb={2}>
          {Object.entries(data.par_role || {}).map(([role, count]) => (
            <Chip key={role} label={`${count} ${role}s`} size="small" variant="outlined"
              sx={{ height: 20, fontSize: { xs: 9, sm: 11 }, textTransform: 'capitalize' }} />
          ))}
        </Box>
        {data.top_vendeurs?.length > 0 && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8, fontSize: 10 }}>
              Top vendeurs du jour
            </Typography>
            {data.top_vendeurs.map((v, i) => (
              <Box key={i} display="flex" alignItems="center" gap={1} py={0.75}
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography sx={{ fontSize: 16, flexShrink: 0 }}>{medals[i] || `${i + 1}.`}</Typography>
                <Box flex={1} minWidth={0}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: { xs: 11, sm: 13 } }}>
                    {v.caissier__first_name} {v.caissier__last_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 11 } }}>
                    {v.nombre_ventes} ventes
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={700} color="success.main" sx={{ flexShrink: 0, fontSize: { xs: 11, sm: 13 } }}>
                  {fmt(v.chiffre_affaires)}
                </Typography>
              </Box>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Section Achats ───────────────────────────────────────────────────────────

function SectionAchats({ data }) {
  if (!data) return null;
  const maxMontant = Math.max(...(data.top_fournisseurs || []).map(f => f.montant_total), 1);
  return (
    <Card>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <SectionTitle icon={<LocalShipping />} title="Achats ce mois" />
        <Box display="flex" gap={3} mb={2} flexWrap="wrap">
          <Box>
            <Typography fontWeight={700} color="primary.main" sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>
              {fmt(data.total_achats_mois)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: 9, sm: 11 } }}>
              {data.nombre_achats_mois} commandes
            </Typography>
          </Box>
        </Box>
        {data.top_fournisseurs?.map((f, i) => (
          <Box key={i} mb={1.5}>
            <Box display="flex" justifyContent="space-between" mb={0.5} gap={1}>
              <Typography variant="body2" noWrap flex={1} sx={{ fontSize: { xs: 11, sm: 13 } }}>{f.fournisseur__nom}</Typography>
              <Typography variant="body2" fontWeight={600} flexShrink={0} sx={{ fontSize: { xs: 11, sm: 13 } }}>{fmt(f.montant_total)}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(f.montant_total / maxMontant) * 100}
              sx={{ height: 5, borderRadius: 3 }}
            />
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [succursales, setSuccursales] = useState([]);
  const [selectedSuccursale, setSelectedSuccursale] = useState('');
  const [isProprietaire, setIsProprietaire] = useState(false);
  const [userName, setUserName] = useState('');

  const fetchDashboard = useCallback(async (succursaleId = '') => {
    setLoading(true);
    setError(null);
    try {
      const params = succursaleId ? { succursale: succursaleId } : {};
      const res = await api.get('depot/dashboard/', { params });
      setData(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        setError("Accès réservé aux managers et propriétaires.");
      } else {
        setError(err.response?.data?.error || 'Erreur lors du chargement du dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setUserName(user.first_name || user.username || '');
      const role = user.role || '';
      setIsProprietaire(role === 'proprietaire' || role === 'super_admin');
    } catch (_) {}

    fetchDashboard();

    api.get('succursales/').then(res => {
      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setSuccursales(list);
    }).catch(() => {});
  }, [fetchDashboard]);

  const handleSuccursaleChange = (e) => {
    const val = e.target.value;
    setSelectedSuccursale(val);
    fetchDashboard(val);
  };

  const kpis = data ? [
    { icon: <TrendingUp />,  label: "CA Aujourd'hui",   value: fmt(data.aujourd_hui?.chiffre_affaires),         color: '#10b981' },
    { icon: <ShoppingCart />,label: "Ventes",            value: fmtNum(data.aujourd_hui?.nombre_ventes),
      sub: data.aujourd_hui?.nombre_ventes_annulees > 0 ? `${data.aujourd_hui.nombre_ventes_annulees} annulées` : undefined,
      color: '#3b82f6' },
    { icon: <AttachMoney />, label: "Panier moyen",      value: fmt(data.aujourd_hui?.panier_moyen),             color: '#f59e0b' },
    { icon: <CreditCard />,  label: "Crédits accordés",  value: fmt(data.aujourd_hui?.total_credit),
      alert: (data.aujourd_hui?.total_credit || 0) > 0, color: '#ef4444' },
    { icon: <Inventory />,   label: "Produits actifs",   value: fmtNum(data.stock?.total_produits_actifs),       color: '#6366f1' },
    { icon: <Warning />,     label: "Ruptures",          value: fmtNum(data.stock?.produits_en_rupture?.count),
      alert: (data.stock?.produits_en_rupture?.count || 0) > 0, color: '#ef4444' },
    { icon: <People />,      label: "Clients",           value: fmtNum(data.clients?.total_clients),
      sub: data.clients?.nouveaux_aujourd_hui > 0 ? `+${data.clients.nouveaux_aujourd_hui} aujourd'hui` : undefined,
      color: '#8b5cf6' },
    { icon: <PointOfSale />, label: "Caisses ouvertes",  value: fmtNum(data.sessions?.sessions_ouvertes?.count), color: '#10b981' },
  ] : [];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, width: '100%', boxSizing: 'border-box' }}>

      {/* ── En-tête ── */}
      <Box
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexDirection={{ xs: 'column', sm: 'row' }}
        mb={3}
        gap={1.5}
      >
        <Box>
          <Typography
            fontWeight={800}
            sx={{ fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.9rem' }, lineHeight: 1.2 }}
          >
            {userName ? `Bonjour, ${userName} 👋` : 'Dashboard'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: { xs: 11, sm: 13 } }}>
            {new Intl.DateTimeFormat('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date())}
          </Typography>
        </Box>

        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          {isProprietaire && succursales.length > 0 && (
            <FormControl size="small" sx={{ minWidth: { xs: 160, sm: 200 } }}>
              <Select
                value={selectedSuccursale}
                onChange={handleSuccursaleChange}
                displayEmpty
                sx={{ fontSize: { xs: 12, sm: 14 } }}
              >
                <MenuItem value="">Toutes les succursales</MenuItem>
                {succursales.filter(s => s.is_active).map(s => (
                  <MenuItem key={s.id} value={s.id.toString()}>{s.nom}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Tooltip title="Rafraîchir">
            <IconButton onClick={() => fetchDashboard(selectedSuccursale)} disabled={loading} size="small">
              {loading ? <CircularProgress size={18} /> : <Refresh />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data ? (
        <Box display="flex" justifyContent="center" py={10}>
          <CircularProgress size={44} />
        </Box>
      ) : data && (
        <Box display="flex" flexDirection="column" gap={2}>

          {/* ── KPI Cards — 2 col xs, 4 col sm, 4 col md+ ── */}
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
            {kpis.map((kpi, i) => (
              <Grid item xs={6} sm={3} md={3} key={i}>
                <KpiCard {...kpi} />
              </Grid>
            ))}
          </Grid>

          {/* ── Tendances — pleine largeur ── */}
          <SectionTendances data={data.tendances} />

          {/* ── Ventes + Stock + Clients — 3 colonnes sur grand écran ── */}
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <SectionVentes data={data.aujourd_hui} />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SectionStock data={data.stock} />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <SectionClients data={data.clients} />
            </Grid>
          </Grid>

          {/* ── Caisses + Équipe ── */}
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
            <Grid item xs={12} sm={6}>
              <SectionSessions data={data.sessions} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SectionEquipe data={data.equipe} />
            </Grid>
          </Grid>

          {/* ── Achats — pleine largeur ── */}
          <SectionAchats data={data.achats} />

        </Box>
      )}
    </Box>
  );
}
